/**
 * THE post-game actions, in one place (fix D / R2).
 *
 * They were `useCallback`s inside `overlay/ResultOverlay/model.ts`; the rail's
 * icon row needs the same four, and a second copy of "New game re-seeks the
 * control that just finished" is exactly the kind of drift that put the result
 * card and the lobby out of step in M1a (P1-3). So they are MODULE FUNCTIONS
 * here and the overlay now calls this hook too — one source, one behaviour.
 *
 * Module functions rather than hooks on purpose: every one of them reads the
 * store with `getState()` at PRESS time, so none of them needs a subscription
 * and all of them have a stable identity. Four leaves can therefore share them
 * without the rail growing four more re-render paths (UX spec §6a). The one
 * reactive thing is the New-game button's LABEL, which is why `useGameActions`
 * exists at all.
 */
import {useShallow} from 'zustand/react/shallow';

import {liveGameStore, useLiveGame} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';
import {START_FEN} from '@/Store/liveGame/state';

import {tcLabel} from '../../pages/home/models/timeControls';
import {track} from '../../pages/home/lobbyAnalytics';
import {resetAnalysis} from '../analysis/analysisStore';
import {navigateTo} from '../navigateRef';
import {resultTabFor} from '../tabs/tabSpec';
import {leaveTournament} from '../tournament/leaveTournament';
import {useV2Ui} from '../../theme/useV2Ui';

/** the exact route + param PlayChess uses (index.js L5150-5152) */
export const REVIEW_ROUTE = 'ChessAnalysis';

/** Caissa's own opponent id in a challenge packet (ChallengeCaissaComponent L144) */
export const CAISSA_ID = '0';

/**
 * What "New game" will do, as three primitives (fix Q). A SHALLOW read: the
 * packet object itself never leaves the store, so this cannot hand the two
 * leaves that take the copy a new object per render.
 */
export type ReplayView = {
  /** a Caissa challenge will be re-sent verbatim */
  bot: boolean;
  opponentName: string;
  /** the replayed packet's own control / mode / start position */
  tcMs: number;
  incMs: number;
  gamePlayMode: string;
  fromPosition: boolean;
};

export const selectReplay = (s: LiveGameState): ReplayView => {
  const request = s.game.request;
  const bot =
    request?.kind === 'challenge' && request.packet.opponentId === CAISSA_ID;
  const packet = bot && request?.kind === 'challenge' ? request.packet : null;
  return {
    bot,
    opponentName: packet?.opponentName || '',
    tcMs: packet?.tcMs ?? 0,
    incMs: packet?.incMs ?? 0,
    gamePlayMode: packet?.gamePlayMode || '',
    fromPosition: !!packet?.fen && packet.fen !== START_FEN,
  };
};

/**
 * The button's own words. A bot replay says what it replays ("Play again"
 * + "Another 60 + 15 Play & Learn vs Caissa 800 from the same position");
 * everything else keeps the control label the result card has always shown.
 */
export const newGameCopy = (
  replay: ReplayView,
  tcMs: number,
  incMs: number,
): {label: string; hint: string} => {
  if (!replay.bot) {
    return {
      label: `New ${tcLabel(tcMs, incMs)}`,
      hint: `A new ${tcLabel(tcMs, incMs)} game`,
    };
  }
  const mode =
    replay.gamePlayMode === 'Play and Learn' ? 'Play & Learn' : 'game';
  const vs = replay.opponentName ? ` vs ${replay.opponentName}` : '';
  const from = replay.fromPosition ? ' from the same position' : '';
  return {
    label: 'Play again',
    hint: `Another ${tcLabel(replay.tcMs, replay.incMs)} ${mode}${vs}${from}`,
  };
};

/**
 * BACK TO THE LOBBY, renamed "Home" (fix N, 2026-10-06) — the owner's ask, and
 * Home now goes all the way home: dropping the game slice is what takes
 * `selectRailState` from 'result' to 'home', but an OPEN TOURNAMENT used to
 * win that fallback (`tournamentOrHome`), landing back on the tournament's own
 * idle view instead of the lobby. Fix N calls `leaveTournament()`
 * (`../tournament/leaveTournament`) right after — the SAME exit the Details
 * tab's "Leave" and the header's back control use, so there is still exactly
 * one way out of a tournament — so Home from a finished tournament game's
 * result (or its idle view) now lands on the home PAGE with the Tournaments
 * tab selected; the next round's `start_game_response` restores the context
 * if the player is paired again (`Store/liveGame/reducers/game.ts`).
 *
 * The three pieces of local UI state that outlive a game go with it: the browsed
 * ply, the dismissed-result flag and the engine.
 *
 * A FINISHED WATCH (or a replay) is left the same way "Stop watching" leaves it:
 * `resetForNewGame` only drops the GAME slice, so without this the rail would
 * stay on 'watch' with a board nobody can get out of. The tile is disabled while
 * the watch is still live, so this only ever ends a finished one.
 */
export const goToLobby = (): void => {
  const ui = useV2Ui.getState();
  ui.setViewPly(null);
  ui.setResultDismissed(false);
  ui.setWatchResultReady(false);
  resetAnalysis();
  const {watch, actions} = liveGameStore.getState();
  if (watch.active || watch.replayGameId) {
    actions.stopWatchingGame();
  }
  actions.resetForNewGame();
  if (liveGameStore.getState().tournament.id) {
    leaveTournament();
  }
};

/**
 * "New game" REPLAYS WHAT THE PLAYER JUST PLAYED (fix Q, owner 2026-10-06:
 * "no matter which mode they were in — Play & Learn, a bot, a library
 * position — it always requests a new random game"). `game.request` is the
 * seek or the challenge packet that produced the finished table, stamped by
 * the `start_game_response` reducer; it is read BEFORE the reset, like the
 * control always was.
 *
 *  - a CAISSA challenge (`opponentId === '0'`: the bot tiles, a library
 *    position, Play & Learn) is re-sent VERBATIM — same strength, same mode,
 *    same start position, same colour request. The challenge-out card takes
 *    over exactly as it does for a rematch.
 *  - a SEEK re-seeks its own control and mode (ResultComponent L681's
 *    `joinGame(moveTimer, Increment)`, now with the mode it was asked for
 *    rather than a hard-coded 'Play').
 *  - a challenge to a REAL user, and a game with no request of ours at all
 *    (a tournament pairing, an accepted incoming challenge, a restore of a
 *    table this client never asked for), fall back to the old behaviour: a
 *    fresh seek at the finished control. "Rematch" is the same-opponent path
 *    for a friend game, so New game stays the "someone else" button there.
 */
export const startNewGame = (): void => {
  const {game, actions} = liveGameStore.getState();
  const {tcMs, incMs, request} = game;
  useV2Ui.getState().setResultDismissed(false);
  resetAnalysis();
  actions.resetForNewGame();
  if (
    request?.kind === 'challenge' &&
    request.packet.opponentId === CAISSA_ID
  ) {
    actions.challengeGame({...request.packet});
    return;
  }
  if (request?.kind === 'seek') {
    actions.seek(request.tcMs, request.incMs, request.gamePlayMode);
    return;
  }
  actions.seek(tcMs, incMs, 'Play');
};

/**
 * "Rematch" is `challenge_game_request` with the colours swapped
 * (`actions.rematch`, M2 Task B); the challenge replaces the result card with
 * the waiting card, and the store drops the finished game inside `challengeGame`
 * (PlayChess L3172-3173).
 */
export const requestRematch = (): void => {
  useV2Ui.getState().setResultDismissed(false);
  resetAnalysis();
  if (liveGameStore.getState().actions.rematch()) {
    track('lobby_rematch', {});
  }
};

/** the full game review (`Screens/ChessAnalysis`), through `rail/navigateRef`. */
export const openReview = (): void => {
  navigateTo(REVIEW_ROUTE, {
    game_id: liveGameStore.getState().game.result?.gameUuid,
  });
};

/**
 * "Go to tournament" — the same reset the post-game button performs, plus
 * landing on the right tab: 'leaderboard' for an arena, 'standings' for a Swiss
 * (old L3259/L6589 on the app side).
 */
export const goToTournament = (): void => {
  const ui = useV2Ui.getState();
  ui.setResultDismissed(false);
  ui.setViewPly(null);
  ui.setRailTab(
    resultTabFor(liveGameStore.getState().tournament.mode === 'arena'),
  );
  resetAnalysis();
  liveGameStore.getState().actions.resetForNewGame();
};

export type GameActions = {
  /** "New 3 + 2", or "Play again" when fix Q will replay a bot game */
  newGameLabel: string;
  /** the detail behind the label: the control, or what is being replayed */
  newGameHint: string;
  onLobby: () => void;
  onNewGame: () => void;
  onRematch: () => void;
  onReview: () => void;
  onGoToTournament: () => void;
};

export function useGameActions(): GameActions {
  // `game_end_response` keeps the control the finished game was played at
  // (L3038-3039), which is what "play another one of these" needs. Two
  // primitives that move once a game — never per move.
  const tcMs = useLiveGame(s => s.game.tcMs);
  const incMs = useLiveGame(s => s.game.incMs);
  // fix Q: one more read that only moves when a game starts. `useShallow` on
  // the three primitives the copy needs, never the packet object itself —
  // a fresh object per render would re-render the two leaves that take this.
  const replay = useLiveGame(useShallow(selectReplay));
  const {label, hint} = newGameCopy(replay, tcMs, incMs);

  return {
    newGameLabel: label,
    newGameHint: hint,
    onLobby: goToLobby,
    onNewGame: startNewGame,
    onRematch: requestRematch,
    onReview: openReview,
    onGoToTournament: goToTournament,
  };
}

export default useGameActions;
